/**
 * The product illustrations of the landing page: small, static, hand-built interfaces in the look
 * of the real application (same tokens, same shapes) over FICTIONAL data. Nothing here is a
 * workspace, none of it is fetched, and none of it is operable: the pages hide it from assistive
 * technology and describe it in words instead. Every visible word is a `landing.*` translation.
 */
import { useTranslation } from 'react-i18next';

import { Icons } from '@/shared/ui';

import styles from './previews.module.css';

const BLUE = '#3b82f6';
const VIOLET = '#8b5cf6';
const TEAL = '#14b8a6';
const AMBER = '#f59e0b';
const ROSE = '#ef4444';

type PersonKey = 'anna' | 'igor' | 'maria' | 'dmitry';
const TONE: Record<PersonKey, string> = { anna: BLUE, igor: TEAL, maria: VIOLET, dmitry: AMBER };

function Avatar({ who }: { who: PersonKey }) {
  const { t } = useTranslation();
  return (
    <span className={styles.avatar} style={{ background: TONE[who] }}>
      {t(`landing.initials.${who}`)}
    </span>
  );
}

type TaskKey = 'settings' | 'api' | 'auth' | 'filters' | 'responsive' | 'release';
type LabelKey = 'design' | 'backend' | 'docs' | 'qa' | 'release';

const LABEL_COLOR: Record<LabelKey, string> = {
  design: VIOLET,
  backend: BLUE,
  docs: TEAL,
  qa: AMBER,
  release: ROSE,
};

function TaskCard({
  task,
  label,
  who,
  overdue,
  extra,
}: {
  task: TaskKey;
  label: LabelKey;
  who: PersonKey;
  overdue?: boolean;
  extra?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`${styles.task} ${extra ? styles.extra : ''}`}>
      <span className={styles.labelChip} style={{ ['--chip' as string]: LABEL_COLOR[label] }}>
        {t(`landing.board.labels.${label}`)}
      </span>
      <span className={styles.taskTitle}>{t(`landing.board.tasks.${task}`)}</span>
      <span className={styles.taskMeta}>
        <span className={`${styles.due} ${overdue ? styles.dueSoon : ''}`}>
          <Icons.clock />
          {t(`landing.board.dates.${task}`)}
        </span>
        <Avatar who={who} />
      </span>
    </div>
  );
}

/** The Kanban board: three columns of realistic IT tasks. `filters` adds the filter bar. */
export function BoardPreview({ filters = false }: { filters?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={styles.board}>
      <div className={styles.toolbar}>
        <strong className={styles.toolbarTitle}>{t('landing.board.title')}</strong>
        {filters && (
          <span className={styles.chips}>
            <span className={styles.pill}>
              <Icons.sliders />
              {t('landing.board.filters')}
            </span>
            <span className={`${styles.pill} ${styles.pillOn}`}>{t('landing.board.mine')}</span>
            <span className={styles.pill}>{t('landing.board.thisWeek')}</span>
          </span>
        )}
      </div>
      <div className={styles.columns}>
        <section className={styles.column}>
          <h4 className={styles.columnTitle}>
            {t('landing.board.columns.planned')}
            <span className={styles.count}>3</span>
          </h4>
          <TaskCard task="settings" label="design" who="maria" />
          <TaskCard task="api" label="docs" who="igor" />
          <TaskCard task="release" label="release" who="dmitry" extra />
        </section>
        <section className={styles.column}>
          <h4 className={styles.columnTitle}>
            {t('landing.board.columns.doing')}
            <span className={styles.count}>2</span>
          </h4>
          <TaskCard task="auth" label="backend" who="anna" overdue />
          <TaskCard task="filters" label="backend" who="igor" />
        </section>
        <section className={styles.column}>
          <h4 className={styles.columnTitle}>
            {t('landing.board.columns.done')}
            <span className={styles.count}>1</span>
          </h4>
          <TaskCard task="responsive" label="qa" who="maria" />
        </section>
      </div>
    </div>
  );
}

/** An opened card, laid over the board in the Boards section. */
export function CardDetailPreview() {
  const { t } = useTranslation();
  return (
    <div className={styles.detail}>
      <span className={styles.labelChip} style={{ ['--chip' as string]: BLUE }}>
        {t('landing.board.labels.backend')}
      </span>
      <strong className={styles.detailTitle}>{t('landing.board.detail.title')}</strong>
      <dl className={styles.facts}>
        <dt>{t('landing.board.detail.status')}</dt>
        <dd>
          <span className={styles.dot} style={{ background: BLUE }} />
          {t('landing.board.columns.doing')}
        </dd>
        <dt>{t('landing.board.detail.due')}</dt>
        <dd>{t('landing.board.dates.auth')}</dd>
        <dt>{t('landing.board.detail.assignee')}</dt>
        <dd>
          <Avatar who="anna" />
          {t('landing.board.detail.person')}
        </dd>
      </dl>
      <span className={styles.checkTitle}>{t('landing.board.detail.checklist')}</span>
      <ul className={styles.checks}>
        <li className={styles.checkDone}>
          <Icons.check />
          {t('landing.board.detail.c1')}
        </li>
        <li className={styles.checkDone}>
          <Icons.check />
          {t('landing.board.detail.c2')}
        </li>
        <li>
          <span className={styles.box} />
          {t('landing.board.detail.c3')}
        </li>
      </ul>
    </div>
  );
}

type ProjectKey = 'site' | 'portal' | 'mobile' | 'analytics';
type StatusKey = 'active' | 'review' | 'planned' | 'done';
type PriorityKey = 'high' | 'medium' | 'low';

const STATUS_COLOR: Record<StatusKey, string> = {
  active: BLUE,
  review: AMBER,
  planned: '#64748b',
  done: '#22c55e',
};
const PRIORITY_COLOR: Record<PriorityKey, string> = { high: ROSE, medium: AMBER, low: TEAL };

const PROJECTS: {
  key: ProjectKey;
  status: StatusKey;
  priority: PriorityKey;
  who: PersonKey;
}[] = [
  { key: 'site', status: 'active', priority: 'high', who: 'anna' },
  { key: 'portal', status: 'review', priority: 'medium', who: 'igor' },
  { key: 'mobile', status: 'planned', priority: 'medium', who: 'maria' },
  { key: 'analytics', status: 'done', priority: 'low', who: 'dmitry' },
];

function ViewSwitch({ active }: { active: 'table' | 'cards' | 'kanban' }) {
  const { t } = useTranslation();
  const items = [
    { key: 'table', icon: <Icons.viewTable /> },
    { key: 'cards', icon: <Icons.viewCards /> },
    { key: 'kanban', icon: <Icons.viewKanban /> },
  ] as const;
  return (
    <span className={styles.viewSwitch}>
      {items.map((item) => (
        <span key={item.key} className={item.key === active ? styles.viewOn : undefined}>
          {item.icon}
          <span className={styles.viewLabel}>{t(`landing.db.views.${item.key}`)}</span>
        </span>
      ))}
    </span>
  );
}

function StatusChip({ status }: { status: StatusKey }) {
  const { t } = useTranslation();
  return (
    <span className={styles.status} style={{ ['--chip' as string]: STATUS_COLOR[status] }}>
      {t(`landing.db.status.${status}`)}
    </span>
  );
}

function PriorityMark({ priority }: { priority: PriorityKey }) {
  const { t } = useTranslation();
  return (
    <span className={styles.priority}>
      <span className={styles.dot} style={{ background: PRIORITY_COLOR[priority] }} />
      {t(`landing.db.priority.${priority}`)}
    </span>
  );
}

/** A universal database shown as a table (hero) - the view switch hints at the other two. */
export function DatabasePreview() {
  const { t } = useTranslation();
  return (
    <div className={styles.db}>
      <div className={styles.toolbar}>
        <strong className={styles.toolbarTitle}>{t('landing.db.title')}</strong>
        <ViewSwitch active="table" />
        <span className={`${styles.pill} ${styles.pillOn} ${styles.savedPill}`}>
          <Icons.eye />
          {t('landing.db.saved')}
        </span>
      </div>
      <div className={styles.table}>
        <div className={`${styles.tr} ${styles.th}`}>
          <span>{t('landing.db.fields.project')}</span>
          <span>{t('landing.db.fields.status')}</span>
          <span className={styles.hideSm}>{t('landing.db.fields.priority')}</span>
          <span className={styles.hideSm}>{t('landing.db.fields.owner')}</span>
          <span>{t('landing.db.fields.due')}</span>
        </div>
        {PROJECTS.map((row) => (
          <div className={styles.tr} key={row.key}>
            <span className={styles.cellStrong}>{t(`landing.db.projects.${row.key}`)}</span>
            <span>
              <StatusChip status={row.status} />
            </span>
            <span className={styles.hideSm}>
              <PriorityMark priority={row.priority} />
            </span>
            <span className={`${styles.person} ${styles.hideSm}`}>
              <Avatar who={row.who} />
              {t(`landing.db.owners.${row.key}`)}
            </span>
            <span className={styles.muted}>{t(`landing.db.dates.${row.key}`)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The same records as cards, next to the saved views: the Databases section. */
export function DatabaseCardsPreview() {
  const { t } = useTranslation();
  const saved = ['all', 'active', 'mine', 'releases'] as const;
  return (
    <div className={`${styles.db} ${styles.dbWide}`}>
      <div className={styles.toolbar}>
        <strong className={styles.toolbarTitle}>{t('landing.db.title')}</strong>
        <ViewSwitch active="cards" />
      </div>
      <div className={styles.dbBody}>
        <nav className={styles.savedList} aria-hidden="true">
          <span className={styles.savedTitle}>{t('landing.db.savedTitle')}</span>
          {saved.map((key) => (
            <span key={key} className={key === 'active' ? styles.savedOn : undefined}>
              {t(`landing.db.savedViews.${key}`)}
            </span>
          ))}
        </nav>
        <div className={styles.cards}>
          {PROJECTS.map((row) => (
            <div className={styles.recordCard} key={row.key}>
              <strong>{t(`landing.db.projects.${row.key}`)}</strong>
              <StatusChip status={row.status} />
              <span className={styles.recordRow}>
                <PriorityMark priority={row.priority} />
              </span>
              <span className={styles.recordFoot}>
                <span className={styles.person}>
                  <Avatar who={row.who} />
                  {t(`landing.db.owners.${row.key}`)}
                </span>
                <span className={styles.muted}>{t(`landing.db.dates.${row.key}`)}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type EventKey = 'sprint' | 'design' | 'sync' | 'release' | 'report' | 'demo' | 'retro';
type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri';

const EVENT_COLOR: Record<EventKey, string> = {
  sprint: BLUE,
  design: VIOLET,
  sync: TEAL,
  release: ROSE,
  report: AMBER,
  demo: BLUE,
  retro: TEAL,
};

const WEEK: { day: DayKey; date: number; events: EventKey[] }[] = [
  { day: 'mon', date: 9, events: ['sprint', 'report'] },
  { day: 'tue', date: 10, events: ['design'] },
  { day: 'wed', date: 11, events: ['sync', 'demo'] },
  { day: 'thu', date: 12, events: ['release'] },
  { day: 'fri', date: 13, events: ['retro'] },
];

function EventBar({ event }: { event: EventKey }) {
  const { t } = useTranslation();
  return (
    <span className={styles.event} style={{ ['--stripe' as string]: EVENT_COLOR[event] }}>
      <span className={styles.eventTime}>
        {event === 'release' && <Icons.flag />}
        {t(`landing.cal.times.${event}`)}
      </span>
      <span className={styles.eventTitle}>{t(`landing.cal.events.${event}`)}</span>
    </span>
  );
}

/** A work week. On a phone the columns turn into an agenda (one day per block). */
export function CalendarPreview() {
  const { t } = useTranslation();
  return (
    <div className={styles.cal}>
      <div className={styles.toolbar}>
        <strong className={styles.toolbarTitle}>{t('landing.cal.title')}</strong>
        <span className={styles.viewSwitch}>
          <span className={styles.viewOn}>{t('landing.cal.week')}</span>
          <span>{t('landing.cal.month')}</span>
        </span>
        <span className={styles.muted}>{t('landing.cal.range')}</span>
      </div>
      <div className={styles.week}>
        {WEEK.map((column) => (
          <div className={styles.dayCol} key={column.day}>
            <span className={styles.dayHead}>
              {t(`landing.cal.days.${column.day}`)}
              <b>{column.date}</b>
            </span>
            {column.events.map((event) => (
              <EventBar key={event} event={event} />
            ))}
          </div>
        ))}
      </div>
      <div className={styles.agenda}>
        {WEEK.slice(0, 4).map((column) => (
          <div className={styles.agendaDay} key={column.day}>
            <span className={styles.agendaHead}>
              {t(`landing.cal.agenda.${column.day}`)}, {column.date}
            </span>
            {column.events.slice(0, 1).map((event) => (
              <EventBar key={event} event={event} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A month: events as thin bars (the Calendar section). Phones get the week preview instead. */
export function MonthPreview() {
  const { t } = useTranslation();
  const days = Array.from({ length: 35 }, (_, index) => index - 2); // 0 and below: previous month
  const bars: Record<number, EventKey[]> = {
    3: ['sprint'],
    5: ['design'],
    10: ['sprint', 'report'],
    12: ['sync'],
    14: ['release'],
    17: ['sprint'],
    19: ['demo'],
    24: ['sprint', 'retro'],
    26: ['report'],
    28: ['release'],
  };
  const heads: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
  return (
    <div className={`${styles.cal} ${styles.month}`}>
      <div className={styles.toolbar}>
        <strong className={styles.toolbarTitle}>{t('landing.cal.title')}</strong>
        <span className={styles.viewSwitch}>
          <span>{t('landing.cal.week')}</span>
          <span className={styles.viewOn}>{t('landing.cal.month')}</span>
        </span>
        <span className={styles.muted}>{t('landing.cal.range')}</span>
      </div>
      <div className={styles.monthGrid}>
        {heads.map((day) => (
          <span key={day} className={styles.monthHead}>
            {t(`landing.cal.days.${day}`)}
          </span>
        ))}
        {days
          .filter((_, index) => index % 7 < 5)
          .map((day) => (
            <div key={day} className={`${styles.monthCell} ${day < 1 ? styles.monthOther : ''}`}>
              <b>{day < 1 ? 28 + day : day}</b>
              {(bars[day] ?? []).map((event) => (
                <span
                  key={event}
                  className={styles.monthEvent}
                  style={{ ['--stripe' as string]: EVENT_COLOR[event] }}
                >
                  {t(`landing.cal.events.${event}`)}
                </span>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

type RoleKey = 'owner' | 'moderator' | 'editor' | 'commenter';

const PEOPLE: { key: PersonKey; role: RoleKey }[] = [
  { key: 'anna', role: 'owner' },
  { key: 'igor', role: 'moderator' },
  { key: 'maria', role: 'editor' },
  { key: 'dmitry', role: 'commenter' },
];

/** A team workspace: its members with their roles and what is shared (the Teamwork section). */
export function TeamPreview() {
  const { t } = useTranslation();
  return (
    <div className={styles.team}>
      <div className={styles.teamTabs}>
        <span>{t('landing.team.personal')}</span>
        <span className={styles.viewOn}>{t('landing.team.title')}</span>
      </div>
      <div className={styles.teamBody}>
        <div>
          <span className={styles.savedTitle}>{t('landing.team.members')}</span>
          <ul className={styles.members}>
            {PEOPLE.map((person) => (
              <li key={person.key}>
                <span className={styles.person}>
                  <Avatar who={person.key} />
                  {t(`landing.team.people.${person.key}`)}
                </span>
                <span className={styles.role}>{t(`landing.team.roles.${person.role}`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <span className={styles.savedTitle}>{t('landing.team.access')}</span>
          <ul className={styles.members}>
            <li>
              <span className={styles.person}>
                <Icons.board />
                {t('landing.team.shared')}
              </span>
              <span className={styles.role}>{t('landing.team.sharedRole')}</span>
            </li>
            <li>
              <span className={styles.person}>
                <Icons.lock />
                {t('landing.team.private')}
              </span>
              <span className={styles.role}>{t('landing.team.privateRole')}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
